import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleSigninComponent } from '../google-signin/google-signin.component';
import { GoogleAuthService, GoogleUser } from '../../services/google-auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-google-auth-demo',
  standalone: true,
  imports: [CommonModule, GoogleSigninComponent],
  template: `
    <div class="demo-container">
      <div class="demo-header">
        <h1>Google Authentication Demo</h1>
        <p>Secure Google Sign-In with backend verification, JWT sessions, and automatic token refresh.</p>
      </div>

      <div class="demo-content">
        <!-- Authentication Status -->
        <div class="status-card">
          <h3>Authentication Status</h3>
          <div class="status-info">
            <div class="status-item">
              <span class="label">Status:</span>
              <span class="value" [class.logged-in]="isLoggedIn" [class.logged-out]="!isLoggedIn">
                {{ isLoggedIn ? 'Authenticated' : 'Not Authenticated' }}
              </span>
            </div>
            <div class="status-item" *ngIf="currentUser">
              <span class="label">User:</span>
              <span class="value">{{ currentUser.name }} ({{ currentUser.email }})</span>
            </div>
            <div class="status-item" *ngIf="userRole">
              <span class="label">Role:</span>
              <span class="value">{{ userRole }}</span>
            </div>
            <div class="status-item" *ngIf="sessionToken">
              <span class="label">Session Token:</span>
              <span class="value token">{{ sessionToken.substring(0, 20) }}...</span>
            </div>
          </div>
        </div>

        <!-- Google Sign-In Component -->
        <div class="signin-card">
          <h3>Google Sign-In Component</h3>
          <p>This is a reusable component that can be dropped into any Angular application:</p>
          
          <div class="component-demo">
            <app-google-signin
              buttonId="demo-google-signin-button"
              [buttonOptions]="{ theme: 'outline', size: 'large' }"
              (signInSuccess)="onSignInSuccess($event)"
              (signInError)="onSignInError($event)"
              (signOutSuccess)="onSignOutSuccess()">
            </app-google-signin>
          </div>

          <div class="code-example" *ngIf="!isLoggedIn">
            <h4>Usage Example:</h4>
            <pre><code>&lt;app-google-signin
  buttonId="my-signin-button"
  [buttonOptions]="&#123; theme: 'outline', size: 'large' &#125;"
  (signInSuccess)="onSignInSuccess($event)"
  (signInError)="onSignInError($event)"
  (signOutSuccess)="onSignOutSuccess()"&gt;
&lt;/app-google-signin&gt;</code></pre>
          </div>
        </div>

        <!-- User Information -->
        <div class="user-card" *ngIf="isLoggedIn && currentUser">
          <h3>User Information</h3>
          <div class="user-details">
            <img [src]="currentUser.picture" [alt]="currentUser.name" class="user-image">
            <div class="user-info">
              <div class="user-field">
                <strong>Name:</strong> {{ currentUser.name }}
              </div>
              <div class="user-field">
                <strong>Email:</strong> {{ currentUser.email }}
              </div>
              <div class="user-field">
                <strong>Google ID:</strong> {{ currentUser.sub }}
              </div>
              <div class="user-field">
                <strong>Given Name:</strong> {{ currentUser.given_name }}
              </div>
              <div class="user-field">
                <strong>Family Name:</strong> {{ currentUser.family_name }}
              </div>
            </div>
          </div>
        </div>

        <!-- API Integration Examples -->
        <div class="api-card" *ngIf="isLoggedIn">
          <h3>API Integration</h3>
          <p>The session token is automatically included in all API requests via the HTTP interceptor.</p>
          
          <div class="api-examples">
            <button (click)="testProtectedAPI()" [disabled]="apiLoading" class="api-button">
              {{ apiLoading ? 'Loading...' : 'Test Protected API' }}
            </button>
            
            <div class="api-result" *ngIf="apiResult">
              <h4>API Response:</h4>
              <pre>{{ apiResult | json }}</pre>
            </div>
            
            <div class="api-error" *ngIf="apiError">
              <h4>API Error:</h4>
              <pre>{{ apiError }}</pre>
            </div>
          </div>
        </div>

        <!-- Features List -->
        <div class="features-card">
          <h3>Features Implemented</h3>
          <ul class="features-list">
            <li>✅ Google Identity Services (GIS) integration with FedCM support</li>
            <li>✅ Backend token verification using google-auth-library</li>
            <li>✅ User lookup by Google Subject ID or email</li>
            <li>✅ Automatic user creation for new users</li>
            <li>✅ Short-lived JWT session tokens (15 minutes)</li>
            <li>✅ Long-lived refresh tokens with HTTP-only cookies</li>
            <li>✅ Automatic token refresh before expiry</li>
            <li>✅ Authorization header injection via HTTP interceptor</li>
            <li>✅ Secure session storage (sessionStorage for tokens)</li>
            <li>✅ Route protection with authentication guards</li>
            <li>✅ Backward compatibility with existing auth system</li>
            <li>✅ Reusable components for other projects</li>
          </ul>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .demo-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .demo-header {
      text-align: center;
      margin-bottom: 40px;
    }

    .demo-header h1 {
      color: #1a73e8;
      margin-bottom: 10px;
    }

    .demo-header p {
      color: #5f6368;
      font-size: 16px;
      max-width: 600px;
      margin: 0 auto;
    }

    .demo-content {
      display: grid;
      gap: 30px;
    }

    .status-card,
    .signin-card,
    .user-card,
    .api-card,
    .features-card {
      background: white;
      border-radius: 8px;
      padding: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid #e0e0e0;
    }

    .status-card h3,
    .signin-card h3,
    .user-card h3,
    .api-card h3,
    .features-card h3 {
      margin: 0 0 16px 0;
      color: #202124;
    }

    .status-info {
      display: grid;
      gap: 12px;
    }

    .status-item {
      display: flex;
      gap: 12px;
    }

    .label {
      font-weight: 600;
      min-width: 120px;
      color: #5f6368;
    }

    .value {
      flex: 1;
      color: #202124;
    }

    .value.logged-in {
      color: #137333;
      font-weight: 600;
    }

    .value.logged-out {
      color: #d93025;
      font-weight: 600;
    }

    .value.token {
      font-family: monospace;
      font-size: 12px;
      background: #f8f9fa;
      padding: 4px 8px;
      border-radius: 4px;
    }

    .component-demo {
      display: flex;
      justify-content: center;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
      margin: 16px 0;
    }

    .code-example {
      margin-top: 20px;
    }

    .code-example h4 {
      margin: 0 0 8px 0;
      color: #5f6368;
    }

    .code-example pre {
      background: #f8f9fa;
      padding: 16px;
      border-radius: 4px;
      overflow-x: auto;
      border: 1px solid #e0e0e0;
    }

    .code-example code {
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #202124;
    }

    .user-details {
      display: flex;
      gap: 20px;
      align-items: flex-start;
    }

    .user-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
    }

    .user-info {
      flex: 1;
    }

    .user-field {
      margin-bottom: 8px;
      line-height: 1.5;
    }

    .api-examples {
      display: grid;
      gap: 16px;
    }

    .api-button {
      background: #1a73e8;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.2s ease;
      justify-self: start;
    }

    .api-button:hover:not(:disabled) {
      background: #1557b0;
    }

    .api-button:disabled {
      background: #5f6368;
      cursor: not-allowed;
    }

    .api-result,
    .api-error {
      margin-top: 16px;
    }

    .api-result h4,
    .api-error h4 {
      margin: 0 0 8px 0;
      color: #5f6368;
    }

    .api-result pre {
      background: #e8f5e8;
      border: 1px solid #34a853;
      color: #137333;
    }

    .api-error pre {
      background: #fce8e6;
      border: 1px solid #ea4335;
      color: #d93025;
    }

    .api-result pre,
    .api-error pre {
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      font-family: monospace;
      font-size: 12px;
      white-space: pre-wrap;
    }

    .features-list {
      list-style: none;
      padding: 0;
      display: grid;
      gap: 8px;
    }

    .features-list li {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;
    }

    .features-list li:last-child {
      border-bottom: none;
    }

    @media (max-width: 768px) {
      .demo-container {
        padding: 16px;
      }

      .user-details {
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .status-item {
        flex-direction: column;
        gap: 4px;
      }

      .label {
        min-width: auto;
      }
    }
  `]
})
export class GoogleAuthDemoComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  currentUser: GoogleUser | null = null;
  userRole: string | null = null;
  sessionToken: string | null = null;
  
  apiLoading = false;
  apiResult: any = null;
  apiError: string | null = null;

  private subscriptions: Subscription[] = [];

  constructor(private googleAuthService: GoogleAuthService) {}

  ngOnInit(): void {
    // Subscribe to auth state changes
    this.subscriptions.push(
      this.googleAuthService.authState$.subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
        this.updateUserInfo();
      })
    );

    // Subscribe to user changes
    this.subscriptions.push(
      this.googleAuthService.user$.subscribe(user => {
        this.currentUser = user;
        this.updateUserInfo();
      })
    );

    // Initial load
    this.updateUserInfo();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private updateUserInfo(): void {
    this.userRole = this.googleAuthService.getCurrentUserRole();
    this.sessionToken = this.googleAuthService.getSessionToken();
  }

  onSignInSuccess(user: GoogleUser): void {
    console.log('Sign-in successful:', user);
    this.apiResult = null;
    this.apiError = null;
  }

  onSignInError(error: any): void {
    console.error('Sign-in error:', error);
    this.apiError = 'Sign-in failed: ' + (error.message || error);
  }

  onSignOutSuccess(): void {
    console.log('Sign-out successful');
    this.apiResult = null;
    this.apiError = null;
  }

  async testProtectedAPI(): Promise<void> {
    this.apiLoading = true;
    this.apiResult = null;
    this.apiError = null;

    try {
      // Example API call using the authentication service
      // This would normally call your actual API endpoints
      const response = {
        message: 'API call successful!',
        user: this.currentUser,
        role: this.userRole,
        timestamp: new Date().toISOString(),
        tokenInfo: {
          hasToken: !!this.sessionToken,
          tokenPreview: this.sessionToken ? this.sessionToken.substring(0, 20) + '...' : null
        }
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      this.apiResult = response;
    } catch (error: any) {
      this.apiError = error.message || 'API call failed';
    } finally {
      this.apiLoading = false;
    }
  }
} 